import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@material-ui/core";
import { useEffect, useState } from "react";
import { useGetLotsQuery } from "features/lots/lotSlice";
import { useGetVendorsQuery } from "features/vendor/vendorSlice";
import { arrayToObject } from "utils/object-util";
import { TabsWrapper } from "components/TabsWrapper/TabsWrapper";
import PricingDetailsTab from "./PricingDetailsTab";
import OtherVendorOptionsTab from "./OtherVendorOptionsTab";
import { PricingLotDetailsDialogStyles } from "./PricingLotDetailsDialogStyles";
import NotificationProvider from "components/NotificationProvider";
import { useUpdateActivityPriceMutation, useUpdateActivityPricePerClientLotMutation, useUpdateActivityPricingManagementMutation } from "features/pricing/pricingSlice";

const PricingLotDetailsDialog = ({ open, onClose, pricingData }) => {
    const classes = PricingLotDetailsDialogStyles();
    const [selectedTab, setSelectedTab] = useState(0);
    const isBundlePricing = !!pricingData?.bundleId;
    const [pricingToEdit, setPricingToEdit] = useState({});
    const [updateActivityPrice, { isLoading: isSaving }] = useUpdateActivityPricePerClientLotMutation();

    useEffect(() => {
        // Keep tab selection valid when dialog context changes.
        setSelectedTab(0);
    }, [open, pricingData?.id, pricingData?.bundleId]);

    useEffect(() => {
        if (!open || !pricingData) {
            setPricingToEdit({});
            return;
        }

        setPricingToEdit({
            id: pricingData?.activityItemPriceId ?? pricingData?.id,
            price: pricingData?.price,
            margin: pricingData?.margin,
            charge: pricingData?.charge,
            priceMode: pricingData?.priceMode,
            activityItemMaterialPriceId: pricingData?.activityItemMaterialPriceId,
            // materialPrices: pricingData?.materialPrices || [],
        });
    }, [open, pricingData]);

    const handleSaveChanges = async () => {
        if (!pricingData) return;

        const materialPrices = (pricingToEdit?.materialPrices || [])
            .map(mp => ({
                id: mp.activityItemMaterialPriceId,
                activityItemMaterialId: mp.activityItemMaterialId,
                cost: parseFloat(mp.cost) || 0
            }));

        const payload = {
            activityItemPriceId: pricingData?.activityItemPriceId ?? pricingData?.id,
            lotId: pricingData?.lotId,
            clientId: pricingData?.clientId,
            price: pricingToEdit?.price,
            charge: pricingToEdit?.charge,
            priceMode: pricingToEdit?.priceMode,
            materialPrices,
        };

        const res = await updateActivityPrice(payload);

        if (!res?.error) {
            NotificationProvider.success("Updated successfully");
            onClose();
        } else {
            NotificationProvider.error("Failed to update");
        }
    };

    let { data: lots } = useGetLotsQuery();
    let { data: vendors } = useGetVendorsQuery();
    lots = lots || [];
    vendors = vendors || [];
    const vendorLookup = arrayToObject(vendors);
    const lotLookup = arrayToObject(lots);

    const tabs = [
        {
            label: "Pricing Details",
            component: <PricingDetailsTab pricingData={pricingData} pricingToEdit={pricingToEdit} setPricingToEdit={setPricingToEdit} />,
        },
        ...(!isBundlePricing
            ? [{
                label: "Other Vendor Options",
                component: <OtherVendorOptionsTab pricingData={pricingData} vendorLookup={vendorLookup} />,
            }]
            : []),
    ];

    return (
        <Dialog open={open} onClose={onClose} fullWidth PaperProps={{ style: { minHeight: "700px", minWidth: "800px" } }}>
            <DialogTitle>
                {pricingData?.activityName}
                <label className={classes.subTitle}>Lot: {lotLookup[pricingData?.lotId]?.name || pricingData?.lotId} </label>
                {!isBundlePricing && (
                    <label className={classes.subTitle}>Vendor: {vendorLookup[pricingData?.vendorId]?.name || pricingData?.vendorId}</label>
                )}
            </DialogTitle>
            <DialogContent >
                <TabsWrapper tabs={tabs} selectedTab={selectedTab} setSelectedTab={setSelectedTab} />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="outlined" color="primary">
                    Close
                </Button>
                <Button onClick={handleSaveChanges} variant="contained" color="primary" disabled={isSaving}>
                    {isSaving ? "Saving..." : "Save Changes"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PricingLotDetailsDialog;
